import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { ApiResponse, PaymentIntent, PaginatedResponse, PaymentEvent } from '../types';
import toast from 'react-hot-toast';

interface PaymentFilters {
    page?: number;
    per_page?: number;
    final_state?: string;
    provider?: string;
    has_risks?: boolean;
    from?: string;
    to?: string;
    search?: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}

export const usePayments = (filters: PaymentFilters = {}) => {
    return useQuery({
        queryKey: ['payments', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value));
                }
            });

            const { data } = await api.get<{ success: boolean; data: PaymentIntent[]; meta: PaginatedResponse<PaymentIntent>['meta'] }>(`/payments?${params}`);
            return { data: data.data, meta: data.meta };
        },
    });
};

export const usePayment = (paymentId: string) => {
    return useQuery({
        queryKey: ['payment', paymentId],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: PaymentIntent }>(`/payments/${paymentId}`);
            return data.data;
        },
        enabled: !!paymentId,
    });
};

export const usePaymentTimeline = (paymentId: string) => {
    return useQuery({
        queryKey: ['payment', paymentId, 'timeline'],
        queryFn: async () => {
            const { data } = await api.get<ApiResponse<{ timeline: any[]; state_history: any[] }>>(`/payments/${paymentId}/timeline`);
            return data.data!;
        },
        enabled: !!paymentId,
    });
};

export const usePaymentEvents = (paymentId: string) => {
    return useQuery({
        queryKey: ['payment', paymentId, 'events'],
        queryFn: async () => {
            const { data } = await api.get<ApiResponse<{ data: PaymentEvent[] }>>(`/payments/${paymentId}/events`);
            return data.data!.data;
        },
        enabled: !!paymentId,
    });
};

export const usePaymentRisks = (paymentId: string) => {
    return useQuery({
        queryKey: ['payment', paymentId, 'risks'],
        queryFn: async () => {
            const { data } = await api.get<ApiResponse<any>>(`/payments/${paymentId}/risks`);
            return data.data!;
        },
        enabled: !!paymentId,
    });
};

export const useResolvePayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            paymentId,
            resolution_note,
            resolve_risks = true,
        }: {
            paymentId: string;
            resolution_note: string;
            resolve_risks?: boolean;
        }) => {
            await api.post(`/payments/${paymentId}/resolve`, {
                resolution_note,
                resolve_risks,
            });
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['payment', variables.paymentId] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            toast.success('Payment resolved successfully');
        },
    });
};
